import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './EditDepartment.css';
import PageTitle from '../../components/UI/PageTitle';
import { getDepartmentById, updateDepartment, getAllUsers, addActivity } from '../../api/Index';

const EditDepartment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [department, setDepartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    budget: '',
    head: '',
    members: []
  });

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  
  // Activity form state
  const [newActivity, setNewActivity] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    budget: '',
    status: 'planned'
  });
  const [addingActivity, setAddingActivity] = useState(false);
  
  // Activity editing state
  const [editingActivityIndex, setEditingActivityIndex] = useState(null);
  const [editedActivity, setEditedActivity] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    budget: '',
    status: 'planned'
  });
  const [updatingActivity, setUpdatingActivity] = useState(false);

  useEffect(() => {
    const fetchDepartmentData = async () => {
      console.log('EditDepartment component mounted');
      console.log('Current location:', location);
      setLoading(true);
      setError(null);
      try {
        const searchParams = new URLSearchParams(location.search);
        const departmentId = searchParams.get('id');
        console.log('Extracted department ID:', departmentId);
        
        if (!departmentId) {
          console.error('No department ID found in URL parameters');
          setError('Department ID not provided');
          return;
        }

        console.log('Calling getDepartmentById with ID:', departmentId);
        const response = await getDepartmentById(departmentId);
        console.log('Department details response:', response);
        
        const departmentData = response.data;
        console.log('Parsed department data:', departmentData);
        setDepartment(departmentData);
        
        // Pre-fill form data
        const formDataToSet = {
          name: departmentData.name || '',
          description: departmentData.description || '',
          budget: departmentData.budget || '',
          head: departmentData.head?._id || '',
          members: departmentData.members?.map(member => member._id) || []
        };
        console.log('Setting form data:', formDataToSet);
        setFormData(formDataToSet);
        
      } catch (err) {
        console.error('Error fetching department details:', err);
        console.error('Error response:', err.response);
        console.error('Error message:', err.message);
        setError(err.response?.data?.msg || err.message || 'Failed to load department details');
      } finally {
        console.log('Finished loading department data');
        setLoading(false);
      }
    };

    fetchDepartmentData();
  }, [location.search]);

  // Fetch users for head and members selection
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getAllUsers();
        setUsers(response.data);
      } catch (err) {
        console.error('Error fetching users:', err);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleMemberChange = (userId) => {
    const updatedMembers = formData.members.includes(userId)
      ? formData.members.filter(id => id !== userId)
      : [...formData.members, userId];
    
    setFormData({
      ...formData,
      members: updatedMembers
    });
  };

  const handleActivityInputChange = (e) => {
    const { name, value } = e.target;
    setNewActivity({
      ...newActivity,
      [name]: value
    });
  };

  const handleEditedActivityChange = (e) => {
    const { name, value } = e.target;
    setEditedActivity({
      ...editedActivity,
      [name]: value
    });
  };

  const startEditingActivity = (index, activity) => {
    setEditingActivityIndex(index);
    setEditedActivity({
      title: activity.title || '',
      description: activity.description || '',
      startDate: activity.startDate ? activity.startDate.split('T')[0] : '',
      endDate: activity.endDate ? activity.endDate.split('T')[0] : '',
      budget: activity.budget || '',
      status: activity.status || 'planned'
    });
  };

  const cancelEditingActivity = () => {
    setEditingActivityIndex(null);
    setEditedActivity({
      title: '',
      description: '',
      startDate: '',
      endDate: '',
      budget: '',
      status: 'planned'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setError(null);
    
    try {
      const searchParams = new URLSearchParams(location.search);
      const departmentId = searchParams.get('id');
      
      if (!departmentId) {
        setError('Department ID not provided');
        return;
      }

      // Validate that head is not in members
      if (formData.head && formData.members.includes(formData.head)) {
        setError('Department head cannot be in members list');
        setUpdating(false);
        return;
      }

      const updateData = {
        name: formData.name,
        description: formData.description,
        budget: parseFloat(formData.budget),
        head: formData.head || null,
        members: formData.members
      };

      const response = await updateDepartment(departmentId, updateData);
      console.log('Department updated successfully:', response.data);
      
      // Navigate back to departments page
      navigate('/departments');
    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Failed to update department');
      console.error('Error updating department:', err);
    } finally {
      setUpdating(false);
    }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    setAddingActivity(true);

    try {
      const searchParams = new URLSearchParams(location.search);
      const departmentId = searchParams.get('id');
      
      if (!departmentId) {
        setError('Department ID not provided');
        return;
      }

      const activityData = {
        title: newActivity.title,
        description: newActivity.description,
        startDate: newActivity.startDate,
        endDate: newActivity.endDate,
        budget: parseFloat(newActivity.budget) || 0,
        status: newActivity.status
      };

      await addActivity(departmentId, activityData);
      
      // Refresh department data to show new activity
      const response = await getDepartmentById(departmentId);
      setDepartment(response.data);
      
      // Reset activity form
      setNewActivity({
        title: '',
        description: '',
        startDate: '',
        endDate: '',
        budget: '',
        status: 'planned'
      });
      
      alert('Activity added successfully!');
    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Failed to add activity');
      console.error('Error adding activity:', err);
    } finally {
      setAddingActivity(false);
    }
  };

  const handleSaveActivity = async () => {
    if (!editedActivity.title.trim()) {
      setError('Activity title is required');
      return;
    }

    setUpdatingActivity(true);

    try {
      const searchParams = new URLSearchParams(location.search);
      const departmentId = searchParams.get('id');
      
      if (!departmentId) {
        setError('Department ID not provided');
        return;
      }

      // For now, we'll update the entire department with modified activities
      // In production, you might want a dedicated updateActivity endpoint
      const updatedActivities = [...department.activities];
      updatedActivities[editingActivityIndex] = {
        ...updatedActivities[editingActivityIndex],
        title: editedActivity.title,
        description: editedActivity.description,
        startDate: editedActivity.startDate || null,
        endDate: editedActivity.endDate || null,
        budget: parseFloat(editedActivity.budget) || 0,
        status: editedActivity.status
      };

      const updateData = {
        name: department.name,
        description: department.description,
        budget: department.budget,
        head: department.head?._id || null,
        members: department.members?.map(member => member._id) || [],
        activities: updatedActivities
      };

      const response = await updateDepartment(departmentId, updateData);
      setDepartment(response.data);
      
      // Reset editing state
      cancelEditingActivity();
      
      alert('Activity updated successfully!');
    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Failed to update activity');
      console.error('Error updating activity:', err);
    } finally {
      setUpdatingActivity(false);
    }
  };

  if (loading) {
    return (
      <div>
        <PageTitle title='Edit Department' />
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p>Loading department details...</p>
          <p style={{ color: '#666', fontSize: '14px' }}>Please wait while we fetch the department data.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageTitle title='Edit Department' />
        <div style={{ padding: '20px', backgroundColor: '#ffe6e6', border: '1px solid #ff0000', borderRadius: '4px', margin: '20px' }}>
          <h3 style={{ color: 'red', margin: '0 0 10px 0' }}>Error Loading Department</h3>
          <p style={{ color: 'red', margin: '0' }}>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            style={{ marginTop: '10px', padding: '8px 16px', backgroundColor: '#ff0000', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!department) {
    return (
      <div>
        <PageTitle title='Edit Department' />
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p>Department not found.</p>
          <p style={{ color: '#666', fontSize: '14px' }}>The requested department could not be found.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div>
        <PageTitle title='Edit Department' />
        <div className="create-department-container">
            <div className="form-container">
                <form className="create-department-form" onSubmit={handleSubmit}>
                    <div className="input-group">
                        <label className="input-label">Department Name *</label>
                        <input
                            type="text"
                            className="input"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Enter department name"
                            required
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Budget *</label>
                        <input
                            type="number"
                            className="input"
                            name="budget"
                            value={formData.budget}
                            onChange={handleInputChange}
                            placeholder="Enter department budget"
                            required
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">Department Head</label>
                        <select
                            className="input"
                            name="head"
                            value={formData.head}
                            onChange={handleInputChange}
                            disabled={loadingUsers}
                        >
                            <option value="">Select Department Head (Optional)</option>
                            {users.map(user => (
                                <option key={user._id} value={user._id}>
                                    {user.name} ({user.email})
                                </option>
                            ))}
                        </select>
                        {loadingUsers && (
                            <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                                Loading users...
                            </small>
                        )}
                    </div>

                    <div className="input-group">
                        <label className="input-label">Department Members</label>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ccc', borderRadius: '4px', padding: '10px' }}>
                            {loadingUsers ? (
                                <p>Loading users...</p>
                            ) : users.length === 0 ? (
                                <p>No users available</p>
                            ) : (
                                users.map(user => (
                                    <div key={user._id} style={{ marginBottom: '8px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={formData.members.includes(user._id)}
                                                onChange={() => handleMemberChange(user._id)}
                                                disabled={formData.head === user._id}
                                                style={{ marginRight: '8px' }}
                                            />
                                            <span style={{ 
                                                color: formData.head === user._id ? '#999' : '#333',
                                                textDecoration: formData.head === user._id ? 'line-through' : 'none'
                                            }}>
                                                {user.name} ({user.email})
                                                {formData.head === user._id && ' - Department Head'}
                                            </span>
                                        </label>
                                    </div>
                                ))
                            )}
                        </div>
                        <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                            Select multiple members. Department head cannot be a member.
                        </small>
                    </div>

                    <div className="input-group">
                        <label className="input-label">Department Description</label>
                        <textarea
                            className="text-area"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="Enter department description"
                        />
                    </div>

                    <button 
                      type="submit" 
                      className='form-button'
                      disabled={updating}
                    >
                      {updating ? 'Updating...' : 'Update Department'}
                    </button>
                </form>

                {/* Activities Section */}
                <div className="activities-section">
                    <h3>Department Activities</h3>
                    
                    {/* Existing Activities */}
                    {department && department.activities && department.activities.length > 0 ? (
                        <div className="current-activities">
                            <h4>Current Activities</h4>
                            <div className="activities-container">
                                {department.activities.map((activity, index) => (
                                    <div key={index} className={`activity-card ${editingActivityIndex === index ? 'activity-card-editing' : ''}`}>
                                        {/* Action Buttons */}
                                        <div className="activity-actions">
                                            {editingActivityIndex === index ? (
                                                <>
                                                    <button
                                                        onClick={handleSaveActivity}
                                                        disabled={updatingActivity}
                                                        className="activity-button activity-button-success"
                                                    >
                                                        {updatingActivity ? 'Saving...' : 'Save Changes'}
                                                    </button>
                                                    <button
                                                        onClick={cancelEditingActivity}
                                                        disabled={updatingActivity}
                                                        className="activity-button activity-button-secondary"
                                                    >
                                                        Cancel
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    onClick={() => startEditingActivity(index, activity)}
                                                    disabled={editingActivityIndex !== null}
                                                    className="activity-button activity-button-primary"
                                                >
                                                    Edit Activity
                                                </button>
                                            )}
                                        </div>

                                        {/* Activity Content */}
                                        <div className="activity-content">
                                            <div className="activity-form-row activity-form-row-2col">
                                                <div className="activity-form-group">
                                                    <label className="input-label">Activity Title *</label>
                                                    {editingActivityIndex === index ? (
                                                        <input
                                                            type="text"
                                                            className="input"
                                                            name="title"
                                                            value={editedActivity.title}
                                                            onChange={handleEditedActivityChange}
                                                            placeholder="Enter activity title"
                                                            required
                                                        />
                                                    ) : (
                                                        <div className="activity-display-field">
                                                            {activity.title}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="activity-form-group">
                                                    <label className="input-label">Status</label>
                                                    {editingActivityIndex === index ? (
                                                        <select
                                                            className="input"
                                                            name="status"
                                                            value={editedActivity.status}
                                                            onChange={handleEditedActivityChange}
                                                        >
                                                            <option value="planned">Planned</option>
                                                            <option value="ongoing">Ongoing</option>
                                                            <option value="completed">Completed</option>
                                                            <option value="cancelled">Cancelled</option>
                                                        </select>
                                                    ) : (
                                                        <div className="activity-display-field">
                                                            <span className={`status-badge status-${activity.status}`}>
                                                                {activity.status}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="activity-form-row activity-form-row-3col">
                                                <div className="activity-form-group">
                                                    <label className="input-label">Start Date</label>
                                                    {editingActivityIndex === index ? (
                                                        <input
                                                            type="date"
                                                            className="input"
                                                            name="startDate"
                                                            value={editedActivity.startDate}
                                                            onChange={handleEditedActivityChange}
                                                        />
                                                    ) : (
                                                        <div className="activity-display-field">
                                                            {activity.startDate ? new Date(activity.startDate).toLocaleDateString() : 'Not set'}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="activity-form-group">
                                                    <label className="input-label">End Date</label>
                                                    {editingActivityIndex === index ? (
                                                        <input
                                                            type="date"
                                                            className="input"
                                                            name="endDate"
                                                            value={editedActivity.endDate}
                                                            onChange={handleEditedActivityChange}
                                                        />
                                                    ) : (
                                                        <div className="activity-display-field">
                                                            {activity.endDate ? new Date(activity.endDate).toLocaleDateString() : 'Not set'}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="activity-form-group">
                                                    <label className="input-label">Budget (KES)</label>
                                                    {editingActivityIndex === index ? (
                                                        <input
                                                            type="number"
                                                            className="input"
                                                            name="budget"
                                                            value={editedActivity.budget}
                                                            onChange={handleEditedActivityChange}
                                                            placeholder="Enter budget amount"
                                                            min="0"
                                                        />
                                                    ) : (
                                                        <div className="activity-display-field">
                                                            {activity.budget ? activity.budget.toLocaleString() : 'Not specified'}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="activity-form-group">
                                                <label className="input-label">Activity Description</label>
                                                {editingActivityIndex === index ? (
                                                    <textarea
                                                        className="text-area"
                                                        name="description"
                                                        value={editedActivity.description}
                                                        onChange={handleEditedActivityChange}
                                                        placeholder="Enter activity description"
                                                        rows="3"
                                                    />
                                                ) : (
                                                    <div className="activity-description-field">
                                                        {activity.description || 'No description provided'}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <p className="no-activities-message">No activities added yet.</p>
                    )}

                    {/* Add New Activity Form */}
                    <form onSubmit={handleAddActivity} className="add-activity-form">
                        <h4>Add New Activity</h4>
                        
                        <div className="activity-form-row activity-form-row-2col">
                            <div className="activity-form-group">
                                <label className="input-label">Activity Title *</label>
                                <input
                                    type="text"
                                    className="input"
                                    name="title"
                                    value={newActivity.title}
                                    onChange={handleActivityInputChange}
                                    placeholder="Enter activity title"
                                    required
                                />
                            </div>

                            <div className="activity-form-group">
                                <label className="input-label">Status</label>
                                <select
                                    className="input"
                                    name="status"
                                    value={newActivity.status}
                                    onChange={handleActivityInputChange}
                                >
                                    <option value="planned">Planned</option>
                                    <option value="ongoing">Ongoing</option>
                                    <option value="completed">Completed</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>
                        </div>

                        <div className="activity-form-row activity-form-row-3col">
                            <div className="activity-form-group">
                                <label className="input-label">Start Date</label>
                                <input
                                    type="date"
                                    className="input"
                                    name="startDate"
                                    value={newActivity.startDate}
                                    onChange={handleActivityInputChange}
                                />
                            </div>

                            <div className="activity-form-group">
                                <label className="input-label">End Date</label>
                                <input
                                    type="date"
                                    className="input"
                                    name="endDate"
                                    value={newActivity.endDate}
                                    onChange={handleActivityInputChange}
                                />
                            </div>

                            <div className="activity-form-group">
                                <label className="input-label">Budget (KES)</label>
                                <input
                                    type="number"
                                    className="input"
                                    name="budget"
                                    value={newActivity.budget}
                                    onChange={handleActivityInputChange}
                                    placeholder="Enter budget amount"
                                    min="0"
                                />
                            </div>
                        </div>

                        <div className="activity-form-group">
                            <label className="input-label">Activity Description</label>
                            <textarea
                                className="text-area"
                                name="description"
                                value={newActivity.description}
                                onChange={handleActivityInputChange}
                                placeholder="Enter activity description"
                                rows="3"
                            />
                        </div>

                        <button 
                            type="submit" 
                            className='form-button add-activity-button'
                            disabled={addingActivity}
                        >
                            {addingActivity ? 'Adding Activity...' : 'Add Activity'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    </div>
  );
};

export default EditDepartment;